/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/** Создаёт единообразную ошибку отмены для SDK-операций без AbortSignal. */
export function createAbortError(): DOMException {
  return new DOMException("Операция отменена", "AbortError");
}

/** Проверяет отмену на границе до или после неотменяемого await. */
export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw createAbortError();
  }
}

/**
 * Завершает ожидание сразу после abort, даже если SDK игнорирует AbortSignal.
 *
 * Обработчики устанавливаются и на исходный promise, поэтому его поздний
 * reject после проигранной гонки не превращается в unhandled rejection.
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async -- async добавит лишнюю promise-обёртку к primitive гонки.
export function raceWithAbort<T>(
  operation: Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  if (signal.aborted) return Promise.reject(createAbortError());

  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => {
      cleanup();
      reject(createAbortError());
    };
    const cleanup = (): void => {
      signal.removeEventListener("abort", onAbort);
    };

    signal.addEventListener("abort", onAbort, { once: true });
    operation.then(
      (value) => {
        cleanup();
        if (signal.aborted) reject(createAbortError());
        else resolve(value);
      },
      (error: unknown) => {
        cleanup();
        if (signal.aborted) reject(createAbortError());
        else reject(error);
      },
    );
  });
}
